import React, { useMemo, useState } from 'react';
import './style.scss';
import BattleActionHands from '../../../../fight/battle/action/hands';
import BattleActionInfo, {
  RoundResults
} from '../../../../fight/battle/action/info';
import { useGlbAnimations } from '../../../../fight/battle/action/hands/useGlbAnimations';
import usePlayer, {
  IUsePlayer
} from '../../../../fight/battle/action/usePlayer';
import {
  CardNames,
  getRandomElement,
  GuildNames,
  NFTBgColors,
  NFTHandColors,
  NFTRankNames
} from 'shared';
import CirclesBackground from '../../../../../general/circlesBackground';
import useWindowSize from '../../../../../../hooks/useWindowSize';
import BattleActionMobile from '../../../../fight/battle/action/adaptiveViews/Mobile';
import BattleActionTablet from '../../../../fight/battle/action/adaptiveViews/Tablet';
import BattleActionPC from '../../../../fight/battle/action/adaptiveViews/PC';
import { useAssets } from '../../../../../../contexts/assets';

interface Props {
  className: string;
}

const LandingFightAction = ({ className }: Props) => {
  const { sound } = useAssets();
  const leftAnimator = useGlbAnimations();
  const rightAnimator = useGlbAnimations();

  // noinspection DuplicatedCode
  const self = usePlayer(
    {
      name: 'Genesis Nifty Hand #4',
      image: '/nft/img/4_.png',
      level: 956,
      rank: {
        name: NFTRankNames.PRO,
        stars: 2,
        cardsAmount: 12
      },
      guild: GuildNames.PAPER,
      colors: {
        hand: NFTHandColors.YELLOW,
        background: NFTBgColors.BLUE
      }
    },
    false,
    {
      rock: 3,
      paper: 3,
      scissors: 3
    },
    null
  );
  const opponent = usePlayer(
    {
      name: 'Genesis Nifty Hand #390',
      image: '/nft/img/390_.png',
      level: 999,
      guild: GuildNames.SCISSORS,
      colors: {
        background: NFTBgColors.NEON,
        hand: NFTHandColors.TANNED
      },
      rank: {
        name: NFTRankNames.PRO,
        stars: 2,
        cardsAmount: 12
      }
    },
    false,
    {
      rock: 3,
      paper: 3,
      scissors: 3
    },
    null
  );
  const [roundResult, setRoundResult] = useState<RoundResults>();

  const [sizeX] = useWindowSize();

  const isTablet = useMemo(() => {
    return sizeX <= 980;
  }, [sizeX <= 980]);

  const isMobile = useMemo(() => {
    return sizeX <= 750;
  }, [sizeX <= 750]);

  const isUltraMobile = useMemo(() => {
    return sizeX <= 450;
  }, [sizeX <= 450]);

  const reset = () => {
    self.setReady(false);
    self.setMovedWith(undefined);
    opponent.setReady(false);
    opponent.setMovedWith(undefined);
    leftAnimator.toShake();
    rightAnimator.toShake();
    setRoundResult(undefined);
  };

  const handleMove = (card: CardNames) => {
    self.setReady(true);
    self.setMovedWith(card);
    const oCard = makeRandomMove(opponent);
    opponent.setReady(true);
    opponent.setMovedWith(oCard);
    leftAnimator.byCard(card, () => sound.battleMove(), 500);
    rightAnimator.byCard(oCard, () => {
      setRoundResult(
        getRoundResult(card, oCard, self.data.guild, opponent.data.guild, true)
      );
      setTimeout(reset, 1500);
    });
  };

  return (
    <div className={className}>
      <CirclesBackground
        scale={isUltraMobile ? 6 : isMobile ? 5 : isTablet ? 4 : 3}
        size='10vw'
        scaleShift={0.5}
        enableLogo
        top='50%'
        left='50%'
        zIndex={1}
      />
      <div className='lfs-fight'>
        <BattleActionHands
          leftColor={self.data.colors.hand}
          rightColor={opponent.data.colors.hand}
          leftAnimator={leftAnimator}
          rightAnimator={rightAnimator}
          size={
            isUltraMobile
              ? 'ultraSmall'
              : isMobile
              ? 'small'
              : isTablet
              ? 'middle'
              : 'big'
          }
        />
        <BattleActionInfo time={-1} results={roundResult} />
        {isMobile ? (
          <BattleActionMobile
            self={self}
            opponent={opponent}
            handleMove={handleMove}
            bet={20}
          />
        ) : isTablet ? (
          <BattleActionTablet
            self={self}
            opponent={opponent}
            handleMove={handleMove}
            bet={20}
          />
        ) : (
          <BattleActionPC
            self={self}
            opponent={opponent}
            handleMove={handleMove}
            bet={20}
          />
        )}
      </div>
    </div>
  );
};

const makeRandomMove = (opponent: IUsePlayer): CardNames => {
  const available = Object.keys(opponent.cards).filter(
    (key) => opponent.cards[key] > 0
  ) as CardNames[];
  return getRandomElement(available);
};

const getRoundResult = (
  sCard: CardNames,
  oCard: CardNames,
  sGuild: GuildNames,
  oGuild: GuildNames,
  ignoreGuild: boolean
): RoundResults => {
  if (sCard === CardNames.ROCK) {
    if (oCard === CardNames.SCISSORS) return 'win';
    if (oCard === CardNames.PAPER) return 'lose';
    return handleDraw(sCard, sGuild, oGuild, ignoreGuild);
  }
  if (sCard === CardNames.PAPER) {
    if (oCard === CardNames.ROCK) return 'win';
    if (oCard === CardNames.SCISSORS) return 'lose';
    return handleDraw(sCard, sGuild, oGuild, ignoreGuild);
  }
  if (oCard === CardNames.PAPER) return 'win';
  if (oCard === CardNames.ROCK) return 'lose';
  return handleDraw(sCard, sGuild, oGuild, ignoreGuild);
};

const handleDraw = (
  card: CardNames,
  sGuild: GuildNames,
  oGuild: GuildNames,
  ignoreGuild: boolean
): RoundResults => {
  if (ignoreGuild || sGuild === oGuild) return 'draw';
  if (card === CardNames.ROCK) {
    if (sGuild === GuildNames.ROCK && oGuild !== GuildNames.ROCK) return 'win';
    if (sGuild !== GuildNames.ROCK && oGuild === GuildNames.ROCK) return 'lose';
  }
  if (card === CardNames.PAPER) {
    if (sGuild === GuildNames.PAPER && oGuild !== GuildNames.PAPER)
      return 'win';
    if (sGuild !== GuildNames.PAPER && oGuild === GuildNames.PAPER)
      return 'lose';
  }
  if (card === CardNames.SCISSORS) {
    if (sGuild === GuildNames.SCISSORS && oGuild !== GuildNames.SCISSORS)
      return 'win';
    if (sGuild !== GuildNames.SCISSORS && oGuild === GuildNames.SCISSORS)
      return 'lose';
  }
  return 'draw';
};

export default LandingFightAction;
